import Icon from "@react-native-vector-icons/material-design-icons/static";
import { useTranslation } from "react-i18next";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { FlatCard } from "../Cards/FlatCard";
import { InsightCallout } from "../DataCards/InsightCallout";
import EmptyState from "../EmptyView";
import ScrollContainer from "../ScrollContainer";
import { LeaderboardCardHeader } from "./LeaderboardCardHeader";
import { LeaderboardJoinCta } from "./LeaderboardJoinCta";
import { LeaderboardList } from "./LeaderboardList";
import { LeaderboardPodiumChart } from "./LeaderboardPodiumChart";
import { LeaderboardSkeleton } from "./LeaderboardSkeleton";
import { getPodiumItems, getRankedItems } from "./helpers";
import type { LeaderboardItem, LeaderboardScope } from "./types";

type Props = {
	items: LeaderboardItem[];
	isLoading?: boolean;
	currentUserItem?: LeaderboardItem;
	showUsersPodium?: boolean;
	numUsersToDisplay?: number;
	scope?: LeaderboardScope;
	error?: { title?: string; description?: string; ctaLabel?: string };
	emptyCopy?: { title?: string; description?: string; ctaLabel?: string };
	insight?: { text?: string; isLoading?: boolean };
	onStart?: () => void;
	onRefresh?: () => void;
	onScrolled?: (scrollY: number) => void;
};

const MINIMUM_LEADERS = 3;

export const LeaderboardContent = ({
	items,
	isLoading = false,
	currentUserItem,
	showUsersPodium = true,
	numUsersToDisplay = 10,
	scope,
	error,
	emptyCopy,
	insight,
	onStart,
	onRefresh,
	onScrolled,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: composes the loading/error/empty/friends-scope/loaded states for the leaderboard body
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const { isMobile } = useDevice();

	const isUnranked = !currentUserItem?.rank;
	const hasInsight = insight !== undefined;
	const isKnownUnranked = currentUserItem !== undefined && isUnranked;

	if (scope === "friends") {
		return (
			<EmptyState
				title={t("leaderboard.friends.comingSoonTitle")}
				subtitle={t("leaderboard.friends.comingSoonDescription")}
				cta={t("leaderboard.friends.comingSoonBadge")}
				image={(_props) => (
					<Box
						width={72}
						height={72}
						borderRadius={36}
						alignItems="center"
						justifyContent="center"
						style={{ backgroundColor: theme.colors.secondaryContainer }}
					>
						<Icon
							name="account-group-outline"
							size={32}
							color={theme.colors.primary}
						/>
					</Box>
				)}
			/>
		);
	}

	if (isLoading) {
		return (
			<ScrollContainer maxWidth="sm" stableScrollbarGutter="both-edges">
				<LeaderboardSkeleton
					numUsersToDisplay={numUsersToDisplay}
					showUsersPodium={showUsersPodium}
					showActionCard={isKnownUnranked || hasInsight}
				/>
			</ScrollContainer>
		);
	}

	if (error) {
		return (
			<EmptyState
				title={error.title ?? t("leaderboard.error.title")}
				subtitle={error.description ?? t("leaderboard.error.description")}
				cta={
					onRefresh
						? (error.ctaLabel ?? t("leaderboard.error.tryAgain"))
						: undefined
				}
				icon="refresh"
				image={(_props) => (
					<Box
						width={72}
						height={72}
						borderRadius={36}
						alignItems="center"
						justifyContent="center"
						style={{ backgroundColor: theme.colors.errorContainer }}
					>
						<Icon name="alert-outline" size={32} color={theme.colors.error} />
					</Box>
				)}
				handleAction={onRefresh}
			/>
		);
	}

	if (items.length < MINIMUM_LEADERS) {
		return (
			<EmptyState
				title={emptyCopy?.title ?? t("leaderboard.empty.title")}
				subtitle={emptyCopy?.description ?? t("leaderboard.empty.description")}
				cta={
					onStart
						? (emptyCopy?.ctaLabel ?? t("leaderboard.empty.startLearning"))
						: undefined
				}
				image={(_props) => (
					<Box
						width={72}
						height={72}
						borderRadius={36}
						alignItems="center"
						justifyContent="center"
						style={{ backgroundColor: theme.colors.elevation.level1 }}
					>
						<Icon
							name="trophy-outline"
							size={32}
							color={theme.colors.primary}
						/>
					</Box>
				)}
				handleAction={onStart}
			/>
		);
	}

	const podiumItems = getPodiumItems(items);
	const rankedItems = getRankedItems(items, {
		showUsersPodium,
		numUsersToDisplay,
	});
	const startRank = showUsersPodium ? MINIMUM_LEADERS + 1 : 1;
	const rankingsTitle = t("leaderboard.rankingsRange", {
		startRank,
		endRank: rankedItems[rankedItems.length - 1]?.rank,
	});

	return (
		<ScrollContainer
			scrollEventThrottle={16}
			maxWidth="sm"
			stableScrollbarGutter="both-edges"
			onScroll={(e) => onScrolled?.(e.nativeEvent.contentOffset.y)}
		>
			<Box gap={isMobile ? 0 : 8} paddingBottom={16}>
				<Box gap={isMobile ? 0 : 1}>
					{showUsersPodium && podiumItems.length > 0 && (
						<FlatCard
							style={
								isUnranked || hasInsight
									? {
											borderBottomLeftRadius: 0,
											borderBottomRightRadius: 0,
										}
									: undefined
							}
						>
							<Box padding={16} gap={8}>
								<Text
									variant="titleMedium"
									style={{
										fontWeight: "600",
										color: theme.colors.onBackground,
									}}
								>
									{t("leaderboard.topPerformers")}
								</Text>
								<LeaderboardPodiumChart
									items={items}
									currentUsername={currentUserItem?.username}
								/>
							</Box>
						</FlatCard>
					)}

					{(isUnranked || hasInsight) && (
						<FlatCard
							style={{
								borderTopLeftRadius: 0,
								borderTopRightRadius: 0,
							}}
						>
							<Box padding={16}>
								{isUnranked ? (
									<LeaderboardJoinCta onStart={onStart} />
								) : (
									<InsightCallout
										variant="flat"
										text={insight?.text}
										isLoading={insight?.isLoading}
										showTrailingIcon={false}
									/>
								)}
							</Box>
						</FlatCard>
					)}
				</Box>

				{rankedItems.length > 0 && (
					<FlatCard>
						<LeaderboardCardHeader title={rankingsTitle} />
						<LeaderboardList
							items={rankedItems}
							currentUsername={currentUserItem?.username}
						/>
					</FlatCard>
				)}
			</Box>
		</ScrollContainer>
	);
};
